import { useEffect, useRef, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageCircle, Loader2, AlertTriangle, RefreshCw } from "lucide-react";

/** Load Meta's SDK exactly once. Race-safe: concurrent callers share one Promise. */
function loadFbSdk() {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.FB) return Promise.resolve(window.FB);
  if (window.__fbSdkPromise) return window.__fbSdkPromise;
  window.__fbSdkPromise = new Promise((resolve, reject) => {
    window.fbAsyncInit = () => resolve(window.FB);
    const s = document.createElement("script");
    s.id = "facebook-jssdk";
    s.async = true;
    s.defer = true;
    s.crossOrigin = "anonymous";
    s.src = "https://connect.facebook.net/en_US/sdk.js";
    s.onerror = () => {
      window.__fbSdkPromise = null;
      reject(new Error("Impossibile caricare l'SDK Meta"));
    };
    document.head.appendChild(s);
  });
  return window.__fbSdkPromise;
}

/**
 * Primary CTA to launch the Meta Embedded Signup v4 popup. Falls back to a
 * clear error state (with Retry) if anything goes wrong or if the flow is
 * cancelled by the user. On success calls `onConnected(publicAccount)`.
 */
export default function WhatsAppEmbeddedSignup({ onConnected, onOpenManual }) {
  const [state, setState] = useState({ loading: true, configured: false, error: null });
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState(null);
  const sessionRef = useRef({}); // captured from postMessage before FB.login callback fires

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/whatsapp/embedded-signup/config");
        setState({ loading: false, configured: data.configured, error: null, cfg: data });
        if (data.configured) {
          try {
            const FB = await loadFbSdk();
            FB.init({
              appId: data.app_id,
              cookie: true,
              xfbml: false,
              version: data.graph_version || "v22.0",
            });
          } catch (e) {
            setState({ loading: false, configured: false, error: e.message });
          }
        }
      } catch (e) {
        setState({ loading: false, configured: false, error: errMsg(e, "Config non disponibile") });
      }
    })();
  }, []);

  useEffect(() => {
    // Meta posts session info to the parent window during the popup lifecycle.
    // Register the listener up-front so we don't miss it.
    const onMessage = (event) => {
      if (typeof event.data !== "string") return;
      if (!/facebook\.com$/.test(new URL(event.origin).hostname || "")) return;
      try {
        const parsed = JSON.parse(event.data);
        if (parsed?.type === "WA_EMBEDDED_SIGNUP") {
          if (parsed.event === "FINISH" && parsed.data) {
            sessionRef.current = {
              waba_id: parsed.data.waba_id,
              phone_number_id: parsed.data.phone_number_id,
              business_id: parsed.data.business_id,
            };
          } else if (parsed.event === "CANCEL") {
            sessionRef.current = { cancelled: true, step: parsed.data?.current_step };
          } else if (parsed.event === "ERROR") {
            sessionRef.current = { errored: true, error_message: parsed.data?.error_message };
          }
        }
      } catch { /* not our message */ }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const launch = async () => {
    setBusy(true);
    setLastError(null);
    sessionRef.current = {};
    try {
      if (!window.FB) throw new Error("SDK Meta non caricato");
      await new Promise((resolve, reject) => {
        window.FB.login((response) => {
          if (!response?.authResponse?.code) {
            const s = sessionRef.current;
            if (s.cancelled) return reject(new Error("Hai chiuso la finestra prima di completare il collegamento."));
            if (s.errored) return reject(new Error(s.error_message || "Errore Meta durante la registrazione."));
            return reject(new Error("Il popup si è chiuso senza restituire un codice."));
          }
          const s = sessionRef.current;
          if (!s.waba_id || !s.phone_number_id) {
            return reject(new Error("Meta non ha restituito WABA ID o Phone Number ID."));
          }
          // Send code + session to backend for server-to-server token exchange.
          api.post("/whatsapp/embedded-signup", {
            code: response.authResponse.code,
            waba_id: s.waba_id,
            phone_number_id: s.phone_number_id,
            business_id: s.business_id,
          }).then((r) => {
            toast.success("WhatsApp collegato correttamente");
            onConnected?.(r.data);
            resolve();
          }).catch((err) => reject(new Error(errMsg(err, "Collegamento server fallito"))));
        }, {
          config_id: state.cfg.config_id,
          response_type: "code",
          override_default_response_type: true,
          extras: { setup: {}, featureType: "whatsapp_business_app_onboarding", sessionInfoVersion: "3" },
        });
      });
    } catch (e) {
      setLastError(e.message || "Errore sconosciuto");
    } finally { setBusy(false); }
  };

  if (state.loading) {
    return (
      <div className="zen-card p-6 flex items-center gap-3 bg-zen-bg/50" data-testid="wa-es-loading">
        <Loader2 size={16} className="animate-spin text-zen-primary"/>
        <span className="text-sm text-zen-ink2">Preparo il collegamento Meta…</span>
      </div>
    );
  }

  if (!state.configured) {
    return (
      <div className="zen-card p-5 bg-zen-info/5 border-zen-info/30" data-testid="wa-es-not-configured">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} className="text-zen-info shrink-0 mt-0.5"/>
          <div className="text-sm text-zen-ink">
            <b>Embedded Signup non ancora configurato.</b><br/>
            L&apos;amministratore deve inserire <code>META_APP_ID</code>, <code>META_APP_SECRET</code> e{" "}
            <code>META_ES_CONFIG_ID</code> nel backend. Nel frattempo puoi usare il collegamento manuale.
          </div>
        </div>
        <div className="mt-4">
          <button
            data-testid="wa-open-manual-fallback"
            onClick={onOpenManual}
            className="text-zen-primary hover:underline text-sm"
          >
            Collega manualmente con token permanente →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="zen-card p-6 text-center bg-gradient-to-br from-white to-zen-bg/40 border-zen-primary/30">
        <div className="w-14 h-14 rounded-full bg-zen-primary/10 text-zen-primary flex items-center justify-center mx-auto mb-4">
          <MessageCircle size={26}/>
        </div>
        <h3 className="font-heading text-xl mb-2">Collega WhatsApp Business</h3>
        <p className="text-sm text-zen-ink2 max-w-md mx-auto mb-5">
          In 3 minuti, senza copia-incolla di token. Accedi a Meta, scegli o crea il tuo
          WhatsApp Business Account, verifica il numero e sei pronto.
        </p>
        <Button
          data-testid="wa-es-launch"
          onClick={launch}
          disabled={busy}
          className="bg-zen-primary hover:bg-zen-primaryHover rounded-full px-8 py-6 text-base"
        >
          {busy ? <><Loader2 size={16} className="mr-2 animate-spin"/>Collegamento in corso…</> : <><MessageCircle size={16} className="mr-2"/>Collega WhatsApp</>}
        </Button>
        {lastError && (
          <div className="mt-5 rounded-xl border border-zen-alert/30 bg-zen-alert/5 p-3 text-left" data-testid="wa-es-error">
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} className="text-zen-alert shrink-0 mt-0.5"/>
              <div className="text-sm text-zen-ink flex-1">
                <b>Collegamento non completato.</b><br/>
                <span className="text-zen-ink2">{lastError}</span>
              </div>
            </div>
            <Button
              data-testid="wa-es-retry"
              size="sm"
              variant="outline"
              onClick={launch}
              className="mt-3 rounded-full"
            >
              <RefreshCw size={13} className="mr-1.5"/> Riprova
            </Button>
          </div>
        )}
        <div className="mt-6 text-xs text-zen-ink2">
          Requisiti Meta: Business verificato, app in modalità Live, permessi{" "}
          <code>whatsapp_business_management</code> + <code>whatsapp_business_messaging</code> approvati.
        </div>
      </div>
      <div className="text-center">
        <button
          data-testid="wa-open-manual"
          onClick={onOpenManual}
          className="text-xs text-zen-ink2 hover:text-zen-primary underline underline-offset-2"
        >
          Ho già un token, collegalo manualmente
        </button>
      </div>
    </div>
  );
}
